import React from "react"
import { Typography, Tag, Space, Tooltip } from "antd"
import { ExclamationCircleOutlined } from "@ant-design/icons"

import Identity from "@components/Identity"
import { getConfig } from "@components/Config"
import { hasAccess } from "@components/Authorization"

import TableRowActions from "../TableRowActions"
import BankAccountVerificationButton from "../../BankAccountVerificationButton"

const IMMUTABLE_BENEFICIARY_NAME = getConfig("IMMUTABLE_BENEFICIARY_NAME")

const LABEL_ACH = "ACH Funding / Distributions"
const LABEL_WIRE = "Wire Funding"
const LABEL_LABEL = "Enabled"
const LABEL_ENTITY = "Entity"
const LABEL_CREATED = "Created By"
const LABEL_INVALID = "Invalid"
const LABEL_IMMUTABLE_ACCOUNT = `All bank accounts of ${IMMUTABLE_BENEFICIARY_NAME} are controlled by Jerry Herrmann.`


const useColumnsConfig = ({
  openBankAccount,
  removeBankAccount,
  isController
}) => {
  const isImmutableBankAccount = bankAccount =>
    bankAccount.beneficiaryName === IMMUTABLE_BENEFICIARY_NAME

  const beneficiaryNameColumnProps = {
    key: "beneficiaryName",
    title: LABEL_ENTITY,
    render: (value, bankAccount) => (
      <Space>
        <Typography.Text>
          {value}
        </Typography.Text>

        {
          isImmutableBankAccount(bankAccount) && (
            <Tooltip
              title={LABEL_IMMUTABLE_ACCOUNT}
              placement="top"
            >
              <ExclamationCircleOutlined />
            </Tooltip>
          )
        }
      </Space>
    ),
    isSearchable: true
  }

  const sourceNameColumnProps = {
    key: "sourceName",
    title: "Account Name",
    render: (value, bankAccount) => (
        <Space>
          <Typography.Link strong onClick={() => openBankAccount(bankAccount)}>
            {value}
          </Typography.Link>

          {
            bankAccount.isInvalid && (
              <Tag color="red" bordered={false}>
                {LABEL_INVALID}
              </Tag>
            )
          }
        </Space>
    ),
    isSearchable: true
  }

  const createdByColumnProps = {
    key: "createdBy",
    title: LABEL_CREATED,
    render: userId => <Identity id={userId} />
  }

  const fundingColumnProps = {
    key: "_funding",
    title: LABEL_WIRE,
    align: "center",
    render: () => <Tag color="green" bordered={false}>{LABEL_LABEL}</Tag>,
  }

  const distributionsColumnProps = {
    key: "_distributions",
    title: LABEL_ACH,
    align: "center",
    render: (_, bankAccount) =>
      <BankAccountVerificationButton bankAccount={bankAccount} />
  }

  const items = [
    { ...beneficiaryNameColumnProps },
    { ...sourceNameColumnProps },
    { ...fundingColumnProps, width: 200 },
    { ...distributionsColumnProps, width: 200 },
    { ...createdByColumnProps },
  ]

  const canDelete = hasAccess(['bank-accounts-write'])

  if (!canDelete) {
    return items
  }

  if (!isController) {
    return items
  }

  items.push({
    key: "actions",
    title: false,
    width: 48,
    render: (_, bankAccount) =>
      <TableRowActions
        bankAccount={bankAccount}
        removeBankAccount={removeBankAccount}
      />
  })

  return items
}

export default useColumnsConfig
